/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

'use strict';

var _ = require('lodash');
var dayjs = require('dayjs');
var utc = require('dayjs/plugin/utc')
var dateTime = require('scripts/utils/date-time');
var deviceUtil = require('scripts/utils/device');
var urlUtil = require('scripts/utils/url');
var can = require('can/util/util');

dayjs.extend(utc)

dateTime = dateTime.default || dateTime;
deviceUtil = deviceUtil.default || deviceUtil;
urlUtil = urlUtil.default || urlUtil;

module.exports = function (helpers, utils) {

    var i18n = utils.i18n;

    return {
        timezoneCode: function () {
            return dateTime.getTimezone().format('z')
        },

        math: function(lvalue, operator, rvalue) {
            lvalue = parseFloat(lvalue);
            rvalue = parseFloat(rvalue);

            return {
                "+": lvalue + rvalue,
                "-": lvalue - rvalue,
                "*": lvalue * rvalue,
                "/": lvalue / rvalue,
                "%": lvalue % rvalue
            }[operator];
        },
        ordinalSuffix: function (number) {
            if (['MC', 'DQ', 'WD'].indexOf(number) !== -1) {
                return "";
            }
            if (typeof number === 'string') {
                number = parseInt(number.replace(/\D/g,''));
            }
            var j = number % 10;
            var k = number % 100;
            if (j == 1 && k != 11) {
                return "st";
            }
            if (j == 2 && k != 12) {
                return "nd";
            }
            if (j == 3 && k != 13) {
                return "rd";
            }
            return "th";
        },

        dateTime: function (date, format, limit) {
            date = _.isFunction(date) ? date() : date;
            format = _.isFunction(format) ? format() : format;
            limit = _.isFunction(limit) ? limit() : limit;
            var now = new Date().valueOf();
            var timeDiff = dateTime.timeDiff(date, now, 'days');
            if (!limit || (limit && timeDiff >= parseInt(limit, 10))) {
                return dateTime.formatDate(date, (format || 'MMM DD, YYYY'));
            } else {
                return dateTime.timeAgo(date);
            }
        },

        dateFormat: function (dateStr, format, options) {
            var locale = _.get(options, 'page.lang') || 'en'
            return dateStr && dayjs(dateStr).locale(locale).format(format);
        },

        socialDate: function (date) {
            return dayjs.utc(date, 'ddd MMM DD H:mm:ss z YYYY');
        },
        timeFromNow: function (date) {
            if (!date) {
                return '';
            }
            return dayjs(date).fromNow();
        },
        isoDateTime: function (date, format) {
            date = _.isFunction(date) ? date() : date;
            var isoDate = date + 'Z';
            format = _.isFunction(format) ? format() : format;
            return dateTime.toLocal(isoDate).format(format || 'DD MMM YYYY');
        },
        timeago: function (timeStamp) {
            if (!timeStamp) {
                return '';
            }

            var actionMoment = dayjs(timeStamp);
            var nowMoment = dayjs();
            var result;

            if (nowMoment.diff(actionMoment, 'days')) {
                return helpers.dateTime(timeStamp, 'DD MMM YYYY');
            }

            if (nowMoment.diff(actionMoment, 'hours')) {
                result = nowMoment.diff(actionMoment, 'hours');
                return result > 1 ? result + ' ' + 'hours ago' : 'an hour ago';
            }

            if (nowMoment.diff(actionMoment, 'minutes') > 5) {
                result = nowMoment.diff(actionMoment, 'minutes');
                return result + ' ' + 'minutes ago';
            }

            return 'Now';
        },

        isActiveLink: function (url, pagePath, options) {
            if (arguments.length < 3) {
                return '';
            }

            var compareWithHash = pagePath ? pagePath.indexOf('#') > -1 : false;
            var op1 = utils.trimUrl(url, compareWithHash);
            var op2 = utils.trimUrl(pagePath, compareWithHash);
            var result = op1 === op2;

            if (!result && pagePath) {
                result = pagePath.indexOf('/' + op1 + '/') > -1;
            }

            if (options.fn) {
                var output = result ? options.fn(this) : options.inverse(this);

                return _.isObject(output) ? output.textContent : output;
            }

            return result;
        },

        takeCupHash: function (hash) {
            var hashParts = hash.split('/');

            return hashParts[hashParts.length - 1] || '';
        },

        preHref: function (url) {
            var params = can.deparam(window.location.search.slice(1));

            if (typeof url === 'function') {
                url = url.call();
            }
            if (deviceUtil.isNative) {
                url = urlUtil.updateQueryString('mode', 'native', url);
            }
            if (params.__ws) {
                url = urlUtil.updateQueryString('__ws', params.__ws, url);
            }
            return url;
        },

        linkTarget: function (showMoreLinkBehavior) {
            showMoreLinkBehavior = _.isFunction(showMoreLinkBehavior) ? showMoreLinkBehavior() : showMoreLinkBehavior;
            return showMoreLinkBehavior === 'Same Window' ? '_self' : '_blank';
        },

        /*
         * How to use:
         * inline - {{i18n 'Hey Joe'}}
         * block - {{#i18n}}Hey Joe{{/i18n}}
         */
        i18n: function (str, options) {
            if (!options) {
                options = str;
                str = '';
            } else {
                str = _.isFunction(str) ? str() : str;
            }

            if (!str && options.fn) {
                str = _.trim(options.fn(this).textContent);
            }

            return i18n(str);
        },

        emptyVal: function (val) {
            if (_.isUndefined(val)) {
                return '';
            } else {
                return val;
            }
        }
    };
};
